// Folder-drop traversal via the (WebKit-prefixed but universally shipped)
// FileSystem Entry API. Structural types instead of lib.dom's experimental
// ones so the traversal is unit-testable with plain fakes.

import type { PickedFile } from "@/lib/uploadPlanner";

export interface FileEntryLike {
  isFile: true;
  isDirectory: false;
  name: string;
  file(ok: (f: File) => void, err?: (e: unknown) => void): void;
}

export interface DirReaderLike {
  readEntries(ok: (entries: EntryLike[]) => void, err?: (e: unknown) => void): void;
}

export interface DirEntryLike {
  isFile: false;
  isDirectory: true;
  name: string;
  createReader(): DirReaderLike;
}

export type EntryLike = FileEntryLike | DirEntryLike;

function entryFile(entry: FileEntryLike): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

function readBatch(reader: DirReaderLike): Promise<EntryLike[]> {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject));
}

async function walk(entry: EntryLike, parent: string, out: PickedFile[]): Promise<void> {
  const relPath = parent ? `${parent}/${entry.name}` : entry.name;
  if (entry.isFile) {
    out.push({ file: await entryFile(entry), relPath });
    return;
  }
  const reader = entry.createReader();
  // Chrome returns at most ~100 entries per readEntries call; keep reading
  // until an empty batch signals exhaustion.
  for (;;) {
    const batch = await readBatch(reader);
    if (batch.length === 0) break;
    for (const child of batch) await walk(child, relPath, out);
  }
}

export async function collectEntries(entries: EntryLike[]): Promise<PickedFile[]> {
  const out: PickedFile[] = [];
  for (const entry of entries) await walk(entry, "", out);
  return out;
}

/**
 * Entries from a drop event's DataTransfer, or null when the browser doesn't
 * expose the Entry API (caller falls back to the flat FileList).
 */
export function entriesFromDataTransfer(dt: DataTransfer): EntryLike[] | null {
  const items = Array.from(dt.items ?? []);
  if (items.length === 0) return null;
  const entries: EntryLike[] = [];
  for (const item of items) {
    const getEntry = (
      item as DataTransferItem & { webkitGetAsEntry?: () => unknown }
    ).webkitGetAsEntry;
    if (typeof getEntry !== "function") return null;
    const entry = getEntry.call(item) as EntryLike | null;
    if (entry) entries.push(entry);
  }
  return entries;
}
